import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { api } from '../lib/api';
import type { Product } from '../lib/api';
import { money } from '../lib/i18n';
import { useSeo } from '../lib/head';
import { Link } from '../lib/router';
import { privateMeta, productMeta } from '../lib/seo';
import { useStore } from '../lib/store';

export function ProductPage() {
  const { slug = '' } = useParams();
  const { lang, currency, t, add, lines } = useStore();

  const [product, setProduct] = useState<Product | null>(null);
  const [failed, setFailed] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [photo, setPhoto] = useState(0);

  useSeo(
    product ? productMeta(product, lang) : failed ? privateMeta(lang, 'notFoundTitle') : null,
  );

  useEffect(() => {
    let cancelled = false;
    setProduct(null);
    setFailed(false);
    setPhoto(0);

    api
      .product(lang, currency, slug)
      .then((p) => {
        if (cancelled) return;
        setProduct(p);
        // Предвыбираем первую доступную фасовку: лишний клик на пустом месте
        setChosen(p.variants.find((v) => v.inStock)?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [lang, currency, slug]);

  if (failed) {
    return (
      <div className="wrap page empty">
        <p>{t.error}</p>
        <Link to="/" className="btn btn--ghost">
          {t.goShopping}
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="wrap page">
        <div className="skeleton" style={{ height: '24rem' }} />
      </div>
    );
  }

  const variant = product.variants.find((v) => v.id === chosen) ?? null;
  const inCart = variant ? lines.some((l) => l.variantId === variant.id) : false;

  // Старый ответ сервера отдавал одно главное фото — на этот случай есть запасной путь
  const photos = product.images?.length
    ? product.images
    : product.image
      ? [{ url: product.image, alt: null }]
      : [];
  const shown = photos[photo] ?? photos[0] ?? null;

  const attrs: Array<[string, string]> = [];
  if (product.harvestYear) attrs.push([t.year, String(product.harvestYear)]);
  if (product.origin) attrs.push([t.origin, product.origin]);
  if (product.region) attrs.push([t.region, product.region]);

  return (
    <div className="wrap page">
      <Link to="/" className="muted" style={{ fontSize: '0.875rem' }}>
        ← {t.catalog}
      </Link>

      <div className="product" style={{ marginTop: '1.5rem' }}>
        <div>
          <div className="product__media">
            {shown ? (
              <img src={shown.url} alt={shown.alt ?? product.title} />
            ) : (
              <span className="card__placeholder">{t.photo}</span>
            )}
          </div>

          {/* Миниатюры — только когда есть из чего выбирать */}
          {photos.length > 1 && (
            <div className="thumbs">
              {photos.map((p, index) => (
                <button
                  key={p.url}
                  type="button"
                  className={`thumbs__item${index === photo ? ' thumbs__item--on' : ''}`}
                  aria-label={`${t.photo} ${index + 1}`}
                  aria-pressed={index === photo}
                  onClick={() => setPhoto(index)}
                >
                  <img src={p.url} alt="" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          {product.category && <span className="tape">{product.category.name}</span>}
          <h1 style={{ marginTop: '1rem' }}>{product.title}</h1>
          {product.short && <p className="lede">{product.short}</p>}

          <div className="variants">
            {product.variants.map((v) => (
              <button
                key={v.id}
                type="button"
                className="variant"
                aria-pressed={v.id === chosen}
                disabled={!v.inStock}
                onClick={() => setChosen(v.id)}
              >
                <span className="variant__name">{v.name}</span>
                {!v.inStock && <span className="muted">{t.soldOut}</span>}
                <span className="variant__price">{money(v.price, currency, lang)}</span>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="btn btn--block"
            disabled={!variant}
            onClick={() => variant && add(variant.id)}
          >
            {inCart ? t.inCart : t.addToCart}
          </button>

          {attrs.length > 0 && (
            <dl className="attrs">
              {attrs.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          )}

          {product.description ? (
            <p style={{ whiteSpace: 'pre-line' }}>{product.description}</p>
          ) : (
            <p className="muted">{t.noDescription}</p>
          )}

          {product.brewing && (
            <>
              <h2 style={{ marginTop: '2rem' }}>{t.brewing}</h2>
              <p style={{ whiteSpace: 'pre-line' }}>{product.brewing}</p>
            </>
          )}

          {product.tastingNotes && product.tastingNotes.length > 0 && (
            <>
              <h2 style={{ marginTop: '2rem' }}>{t.notes}</h2>
              <p className="hand" style={{ fontSize: '1.25rem' }}>
                {product.tastingNotes.join(' · ')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
